import { HTTPError, Options } from 'ky';
import createKyClient from '../../global/services/createKyClient';
import { MS_GRAPH_V1_URI } from '../../Model/CommonProps';
import { ResponseWithHeaders } from '../../Model/ResponseWithHeaders';
import { HttpHeader } from '../../Model/enums/HttpHeader';
import {
  executeHttpMethod,
  extractFilenameFromHeader,
  handleHTTPError,
  HttpMethod,
  parseSuccessResponse,
  parseSuccessResponseWithHeaders,
  streamResponseToFile
} from './helpers/baseApiServiceHelpers';

export const ExecuteEndpoint = async <TResult>(
  endpoint: string,
  httpMethod: HttpMethod,
  options?: Options,
  timeout?: number
): Promise<TResult> => {
  const isMsGraphApi = endpoint.includes(MS_GRAPH_V1_URI);
  const client = createKyClient(isMsGraphApi, timeout);

  try {
    const response = executeHttpMethod(client, httpMethod, endpoint, options);
    const kyResponse = await response;

    return await parseSuccessResponse<TResult>(kyResponse, isMsGraphApi);
  } catch (error) {
    if (error instanceof HTTPError) {
      await handleHTTPError(error, isMsGraphApi);
    }

    throw error;
  }
};

export const ExecuteEndpointWithHeaders = async <TResult>(
  endpoint: string,
  httpMethod: HttpMethod,
  options?: Options,
  timeout?: number
): Promise<ResponseWithHeaders<TResult>> => {
  const isMsGraphApi = endpoint.includes(MS_GRAPH_V1_URI);
  const client = createKyClient(isMsGraphApi, timeout);

  try {
    const response = executeHttpMethod(client, httpMethod, endpoint, options);
    const kyResponse = await response;

    return await parseSuccessResponseWithHeaders<TResult>(kyResponse, isMsGraphApi);
  } catch (error) {
    if (error instanceof HTTPError) {
      await handleHTTPError(error, isMsGraphApi);
    }

    throw error;
  }
};

export const ExecuteFileDownload = async (
  endpoint: string,
  httpMethod: HttpMethod,
  options?: Options,
  defaultFilename = 'download.zip'
): Promise<void> => {
  const client = createKyClient(endpoint.includes(MS_GRAPH_V1_URI), 1800000);

  const updatedOptions = {
    ...options,
    headers: {
      ...options?.headers,
      Accept: 'application/octet-stream'
    }
  };

  const response = executeHttpMethod(client, httpMethod, endpoint, updatedOptions);
  const kyResponse = await response;

  if (!kyResponse.body) {
    throw new Error('Response does not contain a readable stream.');
  }

  const contentDisposition = kyResponse.headers.get(HttpHeader.ContentDisposition);
  const filename = extractFilenameFromHeader(contentDisposition, defaultFilename);

  await streamResponseToFile(kyResponse.body, filename);
};
